import React, { useState } from "react";
import theme from "../data/theme";
import { usePlayerCard } from "../context/PlayerCardContext";

// Shared "open the PlayerCard" affordance for every player list.
//
// Replaces the old muted-gray circled-i glyph, which read as decoration: users
// never recognized it as a control, so full stats / game logs / compare went
// undiscovered. Three ascending bars say "stats" without a label.
//
// Geometry is deliberately identical to the glyph it replaced — 28x28 tap
// target, 16px mark, same flexShrink/inline-flex box — so no row in any list
// changes height or reflows.
const REST = "#60a5fa";                          // brand blue at rest
const ACTIVE = theme.colors.brand.orange;        // brand orange on hover / press

// Inline copy of the mark, for use inside running text (the hint strip). Not a
// control — no tap target, no hover state.
function StatsGlyph({ size = 12, color = REST }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true" style={{ display: "inline-block", verticalAlign: "-1px", flexShrink: 0 }}>
      <rect x="1.6" y="9" width="3.4" height="5.4" rx="1" fill={color} />
      <rect x="6.3" y="5.8" width="3.4" height="8.6" rx="1" fill={color} />
      <rect x="11" y="2.2" width="3.4" height="12.2" rx="1" fill={color} />
    </svg>
  );
}

// One-time discoverability nudge, shown above the first player list a user
// sees. GLOBAL flag, not per-league: the icon means the same thing in every
// league, so it only earns one explanation per person. Unrelated to
// OnboardingSheet, which is a separate first-run surface.
const HINT_KEY = "auctionArchitect_statsHintSeen";

export function StatsHintStrip() {
  const [seen, setSeen] = useState(() => {
    try { return localStorage.getItem(HINT_KEY) === "1"; } catch { return true; }
  });
  if (seen) return null;
  const dismiss = () => {
    try { localStorage.setItem(HINT_KEY, "1"); } catch { /* private mode — hide for this session only */ }
    setSeen(true);
  };
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 6,
      padding: "6px 12px", fontSize: 10.5, lineHeight: 1.35,
      color: "#93c5fd", background: "rgba(96,165,250,0.08)",
      borderBottom: "1px solid rgba(96,165,250,0.2)",
    }}>
      <span style={{ flex: 1, minWidth: 0 }}>
        Tap <StatsGlyph /> on any player for full stats, game logs &amp; comparisons
      </span>
      <span
        onClick={dismiss}
        title="Dismiss"
        style={{ flexShrink: 0, cursor: "pointer", fontSize: 12, lineHeight: 1, padding: "2px 4px", color: "#93c5fd", opacity: 0.7 }}
      >&#10005;</span>
    </div>
  );
}

// stopPointerDown: TierBuilder pills live inside a drag handler that claims
// pointerdown. Swallowing it there keeps a tap on the icon from starting a drag.
export default function StatsButton({ name, source, stopPointerDown = false }) {
  const { openPlayerCard } = usePlayerCard();
  const [active, setActive] = useState(false);
  const color = active ? ACTIVE : REST;
  return (
    <span
      title="Full stats & game logs"
      onPointerDown={e => { if (stopPointerDown) e.stopPropagation(); setActive(true); }}
      onPointerUp={() => setActive(false)}
      onPointerCancel={() => setActive(false)}
      onMouseEnter={() => setActive(true)}
      onMouseLeave={() => setActive(false)}
      onClick={e => { e.stopPropagation(); openPlayerCard(name, {}, source); }}
      style={{
        width: 28, height: 28, display: "inline-flex", alignItems: "center",
        justifyContent: "center", flexShrink: 0, cursor: "pointer",
        ...(stopPointerDown ? { touchAction: "auto" } : {}),
      }}
    >
      <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" style={{ display: "block" }}>
        <rect x="1.6" y="9" width="3.4" height="5.4" rx="1" fill={color} style={{ transition: "fill 0.15s ease" }} />
        <rect x="6.3" y="5.8" width="3.4" height="8.6" rx="1" fill={color} style={{ transition: "fill 0.15s ease" }} />
        <rect x="11" y="2.2" width="3.4" height="12.2" rx="1" fill={color} style={{ transition: "fill 0.15s ease" }} />
      </svg>
    </span>
  );
}
